import { ChangeEvent, useState } from 'react';
import { RefreshCcw, Loader2, X, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { string, StringSchema } from 'yup';

import { checkRepo } from '@/react/portainer/gitops/queries/useCheckRepo';
import { useDebounce } from '@/react/hooks/useDebounce';
import { useGitRepoValidity } from '@/react/portainer/gitops/hooks/useGitRepoValidity';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { Button } from '@@/buttons';
import { useCachedValidation } from '@@/form-components/useCachedTest';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { isBE } from '../feature-flags/feature-flags.service';

import { GitFormModel } from './types';
import { getAuthentication } from './utils';

interface Props {
  value: string;
  onChange(value: string): void;
  onChangeRepositoryValid(value: boolean): void;
  model: GitFormModel;
  createdFromCustomTemplateId?: number;
  stackId?: number;
  errors?: string;
  placeholder?: string;
}

export function GitFormUrlField({
  value,
  onChange,
  onChangeRepositoryValid,
  model,
  createdFromCustomTemplateId,
  stackId,
  errors,
  placeholder = 'e.g. https://github.com/portainer/portainer-compose',
}: Props) {
  const queryClient = useQueryClient();

  const creds = getAuthentication(model);
  const [force, setForce] = useState(false);
  const { errorMessage, isChecking, isValid, query } = useGitRepoValidity({
    url: value,
    creds,
    force,
    tlsSkipVerify: model.TLSSkipVerify,
    createdFromCustomTemplateId,
    stackId,
    enabled: isBE,
    onSettled: onChangeRepositoryValid,
    onAfterSettle: () => setForce(false),
  });

  const [debouncedValue, debouncedOnChange] = useDebounce(value, onChange);

  const fieldErrorMessage = errorMessage || errors;

  return (
    <div className="form-group">
      <div className="col-sm-12">
        <FormControl
          label="Repository URL"
          inputId="stack_repository_url"
          errors={fieldErrorMessage}
          required
        >
          <span className="flex">
            <div className="relative flex-1">
              <Input
                value={debouncedValue}
                type="text"
                name="repoUrlField"
                className="form-control pr-8"
                placeholder={placeholder}
                data-cy="component-gitUrlInput"
                required
                onChange={handleChange}
                id="stack_repository_url"
              />
              {debouncedValue !== '' && (
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center">
                  {isChecking && (
                    <Loader2 className="h-4 w-4 animate-spin stroke-gray-6">
                      <span className="sr-only">Checking repository</span>
                    </Loader2>
                  )}
                  {!isChecking && isValid === false && query.isFetched && (
                    <TooltipWithChildren message="Repository does not exist, or is not accessible">
                      <X className="h-4 w-4 stroke-error-6">
                        <span className="sr-only">
                          Repository does not exist, or is not accessible
                        </span>
                      </X>
                    </TooltipWithChildren>
                  )}
                  {!isChecking && isValid === true && (
                    <TooltipWithChildren message="Repository detected">
                      <Check className="h-4 w-4 stroke-green-6">
                        <span className="sr-only">Repository detected</span>
                      </Check>
                    </TooltipWithChildren>
                  )}
                </div>
              )}
            </div>

            <Button
              onClick={onRefresh}
              data-cy="component-gitUrlRefreshButton"
              size="medium"
              className="vertical-center"
              color="light"
              icon={RefreshCcw}
              title="refreshGitRepo"
              disabled={!model.RepositoryURLValid}
            />
          </span>
        </FormControl>
      </div>
    </div>
  );

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    debouncedOnChange(e.target.value);
  }

  function onRefresh() {
    setForce(true);
    queryClient.invalidateQueries(['git_repo_refs', 'git_repo_search_results']);
  }
}

// Todo: once git form is used only in react, it should be used for validation instead of L40-52
export function useUrlValidation(force: boolean) {
  const existenceTest = useCachedValidation<string, GitFormModel>(
    (url, context) => {
      if (!url) {
        return Promise.resolve(true);
      }

      const model = context.parent as GitFormModel;

      const creds = getAuthentication(model);
      return checkRepo(url, { creds, force });
    }
  );

  return (string() as StringSchema<string, GitFormModel>)
    .url('Invalid Url')
    .required('Repository URL is required')
    .test('repo-exists', 'Repository does not exist', existenceTest);
}
